import { Component, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LucideAngularModule, Settings, Users, Sliders, Plus, Trash2, Edit, X, Save, CheckCircle, AlertOctagon, AlertCircle, Loader } from 'lucide-angular';
import { MrpService } from '../../services/mrp.service';

interface User {
    uuid: string;
    name: string;
    email: string;
    cpf?: string;
    role: 'Admin' | 'Operator' | 'Viewer' | 'ProductSyncOperator' | 'ProductSyncViewer' | 'AlloraSyncOperator' | 'AlloraSyncViewer';
    active: boolean;
}

interface Parameter {
    parameterKey: string;
    parameterValue: string;
    displayName: string;
    description: string;
}

@Component({
    selector: 'app-settings',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.css']
})
export class SettingsComponent {
    private mrpService = inject(MrpService);
    private fb = inject(FormBuilder);

    // Tabs
    activeTab = signal<'parameters' | 'users'>('parameters');

    // Icons
    readonly icons = { Settings, Users, Sliders, Plus, Trash2, Edit, X, Save, CheckCircle, AlertOctagon, AlertCircle, Loader };

    // Parameters
    // Parameters

    // Users
    users = signal<User[]>([]);

    isUserModalOpen = signal<boolean>(false);
    editingUser = signal<User | null>(null);
    userForm: FormGroup;
    isLoadingUsers = signal<boolean>(false);

    constructor() {
        this.userForm = this.fb.group({
            name: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            password: [''],
            role: ['Viewer', Validators.required],
            cpf: [''],
            active: [true]
        });

        // Load initial data
        this.loadUsers();
        this.loadParameters();
    }

    loadUsers() {
        this.isLoadingUsers.set(true);
        this.mrpService.getUsers().subscribe({
            next: (data) => {
                const mapped: User[] = data.map((u: any) => ({
                    uuid: u.uuid,
                    name: u.name,
                    email: u.email,
                    cpf: u.cpf,
                    role: u.role,
                    active: u.active
                }));
                this.users.set(mapped);
                this.isLoadingUsers.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar usuários', err);
                this.isLoadingUsers.set(false);
            }
        });
    }

    // Tab Switching
    switchTab(tab: 'parameters' | 'users') {
        this.activeTab.set(tab);
        if (tab === 'users') {
            this.loadUsers();
        } else if (tab === 'parameters') {
            this.loadParameters();
        }
    }

    // Parameters Logic
    parameters = signal<Parameter[]>([]);
    isLoadingParameters = signal<boolean>(false);

    loadParameters() {
        this.isLoadingParameters.set(true);
        this.mrpService.getParameters().subscribe({
            next: (data) => {
                this.parameters.set(data);
                this.isLoadingParameters.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar parâmetros', err);
                this.showModal('error', 'Erro', 'Falha ao carregar parâmetros.');
                this.isLoadingParameters.set(false);
            }
        });
    }

    saveAllParameters() {
        this.showModal('confirm', 'Salvar Alterações', 'Deseja realmente salvar? Pode demorar alguns minutos para surtir efeito.', () => {
            this.isLoadingParameters.set(true);

            const payload = this.parameters().map(p => ({
                parameterKey: p.parameterKey,
                parameterValue: p.parameterValue
            }));

            this.mrpService.updateParameters(payload).subscribe({
                next: () => {
                    this.showModal('success', 'Sucesso', 'Parâmetros atualizados com sucesso!');
                    this.isLoadingParameters.set(false);
                },
                error: (err) => {
                    console.error('Erro ao salvar parâmetros', err);
                    const msg = err.error?.message || err.message || 'Erro desconhecido ao salvar parâmetros.';
                    this.showModal('error', 'Erro', msg);
                    this.isLoadingParameters.set(false);
                }
            });
        });
    }

    updateParameterValue(param: Parameter, event: Event) {
        const val = (event.target as HTMLInputElement).value;
        // Update local model so save button uses new value
        param.parameterValue = val;
    }

    // Modal System
    modalState = signal<{
        type: 'confirm' | 'success' | 'error',
        title: string,
        message: string,
        confirmLabel?: string,
        cancelLabel?: string,
        onConfirm?: () => void
    } | null>(null);

    showModal(type: 'confirm' | 'success' | 'error', title: string, message: string, onConfirm?: () => void) {
        this.modalState.set({
            type,
            title,
            message,
            confirmLabel: type === 'confirm' ? 'Sim' : 'OK',
            cancelLabel: type === 'confirm' ? 'Cancelar' : undefined,
            onConfirm
        });
    }

    closeModal() {
        this.modalState.set(null);
    }

    // Users Logic
    openUserModal(user?: User) {
        this.isUserModalOpen.set(true);
        if (user) {
            this.editingUser.set(user);
            this.userForm.patchValue({
                name: user.name, // Preliminary fill
                email: user.email,
                role: user.role,
                cpf: user.cpf,
                active: user.active
            });
            this.userForm.disable(); // Disable while loading details

            this.mrpService.getUserById(user.uuid).subscribe({
                next: (detail: any) => {
                    this.userForm.patchValue({
                        name: detail.name,
                        email: detail.email,
                        cpf: detail.cpf,
                        role: detail.role,
                        active: detail.active
                    });
                    this.userForm.enable();
                    // Password field is now ENABLED for editing as requested
                },
                error: (err) => {
                    console.error('Erro ao carregar detalhes do usuário', err);
                    this.showModal('error', 'Erro', 'Falha ao carregar detalhes do usuário.');
                    this.closeUserModal();
                }
            });
        } else {
            this.editingUser.set(null);
            this.userForm.reset({ role: 'Viewer', active: true });
            this.userForm.enable();
            // Password field is ENABLED for creation
        }
    }

    closeUserModal() {
        this.isUserModalOpen.set(false);
        this.editingUser.set(null);
    }

    saveUser() {
        if (this.userForm.invalid) return;

        this.isLoadingUsers.set(true);
        const formVal = this.userForm.value;

        if (this.editingUser()) {
            // Update
            const id = this.editingUser()!.uuid;
            const payload = {
                name: formVal.name,
                email: formVal.email,
                cpf: formVal.cpf,
                password: formVal.password, // Sending password if provided
                role: formVal.role,
                active: formVal.active
            };

            this.mrpService.updateUser(id, payload).subscribe({
                next: () => {
                    this.loadUsers();
                    this.closeUserModal();
                    this.showModal('success', 'Sucesso', 'Usuário atualizado com sucesso!');
                },
                error: (err) => {
                    console.error('Erro ao atualizar usuário', err);
                    const msg = err.error?.message || err.message || 'Erro desconhecido ao atualizar usuário.';
                    this.showModal('error', 'Erro', msg);
                    this.isLoadingUsers.set(false);
                }
            });
        } else {
            // Create
            const payload = {
                name: formVal.name,
                email: formVal.email,
                cpf: formVal.cpf,
                password: formVal.password,
                role: formVal.role
            };

            this.mrpService.createUser(payload).subscribe({
                next: () => {
                    this.loadUsers();
                    this.closeUserModal();
                    this.showModal('success', 'Sucesso', 'Usuário criado com sucesso!');
                },
                error: (err) => {
                    console.error('Erro ao criar usuário', err);
                    const msg = err.error?.message || err.message || 'Erro desconhecido ao criar usuário.';
                    this.showModal('error', 'Erro', msg);
                    this.isLoadingUsers.set(false);
                }
            });
        }
    }

    getRoleLabel(role: string): string {
        switch (role) {
            case 'Admin': return 'Administrador';
            case 'Operator': return 'Operador';
            case 'Viewer': return 'Visualizador';
            case 'ProductSyncOperator': return 'Operador de Replicação';
            case 'ProductSyncViewer': return 'Visualizador de Replicação';
            case 'AlloraSyncOperator': return 'Operador Allora';
            case 'AlloraSyncViewer': return 'Visualizador Allora';
            default: return role;
        }
    }

    deleteUser(id: string) {
        this.showModal('confirm', 'Excluir Usuário', 'Tem certeza que deseja excluir este usuário?', () => {
            this.isLoadingUsers.set(true);
            this.mrpService.deleteUser(id).subscribe({
                next: () => {
                    this.loadUsers();
                    this.showModal('success', 'Sucesso', 'Usuário excluído com sucesso!');
                },
                error: (err) => {
                    console.error('Erro ao excluir usuário', err);
                    const msg = err.error?.message || err.message || 'Erro desconhecido ao excluir usuário.';
                    this.showModal('error', 'Erro', msg);
                    this.isLoadingUsers.set(false);
                }
            });
        });
    }
}
